import {
  Button,
  Card,
  CellText,
  Checkbox,
  DataTable,
  EmptyState,
  FormField,
  ListToolbar,
  Select,
  Text,
  type FilterChip,
  type DataTableColumn,
} from '@ui';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useFacetTexts } from '../../../lib/facets';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useResetUrlState, useUrlState } from '../../../lib/useUrlState';
import { useInvoices, type InvoiceListItem, type InvoiceStatus } from '../api';
import { useInvoiceRowActions } from '../components/InvoiceRowActions';
import { InvoiceBadge } from '../components/SaleBadges';
import { formatEuro } from '../format';
import styles from './SalesPages.module.css';

const STATUSES = ['draft', 'final', 'cancelled'] as const;
const FILTER_KEYS = ['status', 'ueberfaellig'] as const;

/** Invoices, filterable by status and overdue ones (INVC-011); `?ueberfaellig=1` from the dashboard. */
export function InvoicesPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useUrlState<InvoiceStatus | ''>('status', '', STATUSES);
  const [overdueParam, setOverdueParam] = useUrlState('ueberfaellig', '');
  const resetFilters = useResetUrlState(FILTER_KEYS);
  const facetTexts = useFacetTexts(resetFilters);
  const columnSort = useColumnSort();
  const { rowActions, dialogs } = useInvoiceRowActions();
  const overdue = overdueParam === '1';
  const invoices = useInvoices({ ...(status ? { status } : {}), overdue });
  const rows = invoices.data ?? [];
  const filtered = status !== '' || overdue;
  const empty = !invoices.isPending && rows.length === 0 && !filtered;
  // Same label as the InvoiceBadge, for sorting by status.
  const stateLabel = (i: InvoiceListItem) =>
    i.kind === 'cancellation'
      ? t('sales.invoice.badge.cancellation')
      : i.status === 'draft'
        ? t('sales.invoice.badge.draft')
        : i.status === 'cancelled'
          ? t('sales.invoice.badge.cancelled')
          : i.paid
            ? t('sales.invoice.badge.paid')
            : i.overdue
              ? t('sales.invoice.badge.overdue')
              : t('sales.invoice.badge.open');

  const chips: FilterChip[] = [
    ...(status
      ? [
          {
            key: 'status',
            label: t('sales.filters.chipStatus', { status: t(`sales.invoice.status.${status}`) }),
            onRemove: () => setStatus(''),
          },
        ]
      : []),
    ...(overdue
      ? [
          {
            key: 'overdue',
            label: t('sales.invoice.badge.overdue'),
            onRemove: () => setOverdueParam(''),
          },
        ]
      : []),
  ];

  const columns: DataTableColumn<InvoiceListItem>[] = [
    {
      key: 'number',
      header: t('sales.invoice.number'),
      primary: true,
      cell: (i) => (
        <CellText
          main={i.number ?? t('sales.invoice.badge.draft')}
          sub={i.kind === 'cancellation' ? t('sales.invoice.cancellationOf') : null}
        />
      ),
      sortValue: (i) => i.number,
    },
    {
      key: 'customer',
      header: t('sales.columns.customer'),
      cell: (i) => i.customer?.name ?? null,
      sortValue: (i) => i.customer?.name,
    },
    {
      key: 'issued',
      header: t('sales.invoice.issueDate'),
      cell: (i) => (i.issue_date ? formatDate(i.issue_date) : null),
      sortValue: (i) => i.issue_date,
    },
    {
      key: 'due',
      header: t('sales.invoice.dueDate'),
      mobile: 'hidden',
      cell: (i) => (i.due_date ? formatDate(i.due_date) : null),
      sortValue: (i) => i.due_date,
    },
    {
      key: 'total',
      header: t('sales.columns.total'),
      align: 'end',
      cell: (i) => (
        <Text as="span" numeric>
          {formatEuro(i.total_cents)}
        </Text>
      ),
      sortValue: (i) => i.total_cents,
    },
    {
      key: 'status',
      header: t('sales.columns.status'),
      mobile: 'subtitle',
      cell: (i) => (
        <InvoiceBadge
          status={i.status}
          paid={i.paid}
          overdue={i.overdue}
          cancellation={i.kind === 'cancellation'}
        />
      ),
      sortValue: stateLabel,
    },
  ];

  const sortMenu = useSortMenu(columns, rows, columnSort);

  return (
    <div className={styles.page}>
      {!empty && (
        <ListToolbar
          sort={sortMenu}
          facets={{
            ...facetTexts,
            items: [
              {
                type: 'select',
                key: 'status',
                label: t('sales.filters.status'),
                value: status,
                options: STATUSES.map((s) => ({
                  value: s,
                  label: t(`sales.invoice.status.${s}`),
                })),
                onChange: (v) => setStatus(v as InvoiceStatus | ''),
              },
              {
                type: 'toggle',
                key: 'overdue',
                label: t('sales.invoice.overdueOnly'),
                active: overdue,
                onChange: (on) => setOverdueParam(on ? '1' : ''),
              },
            ],
          }}
          filters={{
            label: t('common.filter'),
            activeCount: chips.length,
            content: (
              <>
                <FormField label={t('sales.filters.status')}>
                  {(p) => (
                    <Select
                      {...p}
                      value={status}
                      onChange={(e) => setStatus(e.target.value as InvoiceStatus | '')}
                      options={[
                        { value: '', label: t('sales.filters.allStatuses') },
                        ...STATUSES.map((s) => ({
                          value: s,
                          label: t(`sales.invoice.status.${s}`),
                        })),
                      ]}
                    />
                  )}
                </FormField>
                <Checkbox
                  label={t('sales.invoice.overdueOnly')}
                  checked={overdue}
                  onChange={(e) => setOverdueParam(e.target.checked ? '1' : '')}
                />
              </>
            ),
          }}
          chips={{
            chips,
            removeLabel: (label) => t('common.removeFilter', { label }),
            resetLabel: t('common.resetAll'),
            onReset: resetFilters,
          }}
        />
      )}
      <Card padding="none">
        {invoices.error ? (
          <Text tone="danger" role="alert" style={{ padding: 'var(--space-4)' }}>
            {errorMessage(invoices.error)}
          </Text>
        ) : (
          <DataTable
            caption={t('sales.invoice.caption')}
            rows={rows}
            rowKey={(i) => i.id}
            rowHref={(i) => `/verkauf/rechnungen/${i.id}`}
            rowActions={rowActions}
            {...columnSort}
            loading={invoices.isPending}
            columns={columns}
            empty={
              filtered ? (
                <EmptyState
                  title={t('common.noResults')}
                  actions={
                    <Button variant="secondary" onClick={resetFilters}>
                      {t('common.resetFilters')}
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  title={t('sales.invoice.emptyTitle')}
                  description={t('sales.invoice.empty')}
                />
              )
            }
          />
        )}
      </Card>
      {dialogs}
    </div>
  );
}
